import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ApiResponse } from '../interfaces/api-response.interface';
import { ForgotPasswordResponse, LoginResponse, ResetPasswordResponse, ResendMfaCodeResponse, MfaResponse } from '../interfaces/auth-response.interface';
import { MsalAuthService } from './msal-auth.service';
import { UserService } from './user.service';

const TOKEN_KEY = 'ks_token';
const EXP_KEY = 'ks_token_exp';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private api = inject(ApiService);
  private router = inject(Router);
  private msalAuth = inject(MsalAuthService);
  private userService = inject(UserService);
  private _lastLoginUserId: number | null = null;
  private _lastLoginEmail: string | null = null;

  login(email: string, password: string): Observable<ApiResponse<LoginResponse>> {
    return this.api.post<ApiResponse<LoginResponse>>('/auth/login', { email, password }).pipe(
      tap(res => {
        if (res.isSuccess && res.data?.userId) {
          this._lastLoginUserId = res.data.userId;
          this._lastLoginEmail = email;
        }
      })
    );
  }

  get lastLoginUserId(): number | null {
    return this._lastLoginUserId;
  }

  get lastLoginEmail(): string | null {
    return this._lastLoginEmail;
  }

  logout(): void {
    this.userService.clearUserFromLocal();
    if (this.msalAuth.isSSOUser()) {
      this.msalAuth.logout();
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(EXP_KEY);
      this.router.navigate(['/auth/login']);
    }
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    if (this.msalAuth.isSSOUser()) {;
      if (this.msalAuth.isTokenValid()) {
        return true;
      }
      try {
        this.msalAuth.trySilentSSO();
        return this.msalAuth.isTokenValid();
      } catch {
        return false;
      }
    } else {
      const token = this.getToken();
      const exp = Number(localStorage.getItem(EXP_KEY) || '0');
      return !!token && Date.now() < exp;
    }
  }

  verifyMfa(code: string, userId: number, email: string): Observable<ApiResponse<MfaResponse>> {
    const payload = { userId, email, code };
    return this.api.post<ApiResponse<MfaResponse>>('/auth/verifyMfa', payload).pipe(
      tap(res => {
        if (res.isSuccess && res.data?.token) {
          const token = res.data.token;
          const expiresAt = Date.now() + 3600 * 1000;
          localStorage.setItem(TOKEN_KEY, token);
          localStorage.setItem(EXP_KEY, String(expiresAt));
          localStorage.setItem('ks_userid', String(userId));
          localStorage.setItem('ks_email', email);
        }
      })
    );
  }

  resendMfaCode(userId: number, email: string): Observable<ApiResponse<ResendMfaCodeResponse>> {
    const payload = { userId, email };
    return this.api.post<ApiResponse<ResendMfaCodeResponse>>('/auth/resendmfa', payload);
  }

  sendPasswordResetEmail(email: string): Observable<ApiResponse<ForgotPasswordResponse>> {
    return this.api.post<ApiResponse<ForgotPasswordResponse>>('/auth/forgotpassword', { email });
  }

  resetPassword(email: string, newPassword: string, token: string): Observable<ApiResponse<ResetPasswordResponse>> {
    const payload = { email, newPassword, token };
    return this.api.post<ApiResponse<ResetPasswordResponse>>('/auth/resetpassword', payload);
  }

  forceActivate(payload: { email: string; newPassword: string; tempPassword: string; token: string }): Observable<ApiResponse<ResetPasswordResponse>> {
    return this.api.post<ApiResponse<ResetPasswordResponse>>('/auth/force-activate', payload);
  }
}